import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SeoScript from "@/components/feature/SeoScript";
import AnimeNavbar from "@/components/feature/AnimeNavbar";
import AnimeFooter from "@/components/feature/AnimeFooter";
import usePageTitle from "@/hooks/usePageTitle";
import { episodes, siteName } from "@/mocks/anime";

const heroBg =
  "https://readdy.ai/api/search-image?query=anime%20illustration%20of%20a%20stack%20of%20open%20books%20and%20glowing%20page%20fragments%20floating%20in%20a%20warm%20library%20at%20dusk%2C%20large%20dark%20amber%20and%20brown%20areas%20for%20text%20contrast%2C%20ink%20wash%20style%2C%20cinematic%20manga%20aesthetic%2C%20clean%20composition&width=1600&height=600&seq=episodes-hero&orientation=landscape";

const tagColor: Record<string, string> = {
  开端: "bg-primary-100 text-primary-700",
  修行: "bg-accent-100 text-accent-700",
  校园: "bg-secondary-100 text-secondary-700",
  羁绊: "bg-accent-100 text-accent-700",
  冲突: "bg-primary-100 text-primary-700",
  转折: "bg-accent-100 text-accent-700",
  身世: "bg-secondary-100 text-secondary-700",
  高潮: "bg-primary-100 text-primary-700",
  揭示: "bg-accent-100 text-accent-700",
  低谷: "bg-secondary-100 text-secondary-700",
  回归: "bg-accent-100 text-accent-700",
  结局: "bg-primary-100 text-primary-700",
};

function EpisodeList() {
  const tags = ["全部", ...Array.from(new Set(episodes.map((e) => e.tag)))];
  const [active, setActive] = useState("全部");

  const filtered = useMemo(
    () => (active === "全部" ? episodes : episodes.filter((e) => e.tag === active)),
    [active]
  );

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 flex items-center gap-1 text-sm text-foreground-600">
          <i className="ri-filter-3-line"></i>
          按章节筛选
        </span>
        {tags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActive(t)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
              active === t
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((ep) => (
          <article
            key={ep.ep}
            className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:-translate-y-0.5 hover:border-primary-200"
          >
            <div className="flex items-center justify-between">
              <span className="font-heading text-xl text-primary-500">EP.{ep.ep}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs ${tagColor[ep.tag] || "bg-secondary-100 text-secondary-700"}`}>
                {ep.tag}
              </span>
            </div>
            <h2 className="mt-3 text-base font-medium text-foreground-900">{ep.title}</h2>
            <p className="mt-2 flex-1 text-sm leading-6 text-foreground-600">{ep.desc}</p>
            <div className="mt-4 flex items-start gap-2 rounded-md bg-secondary-50 px-3 py-2.5">
              <i className="ri-star-smile-line mt-0.5 text-secondary-600"></i>
              <span className="text-xs leading-5 text-secondary-800">{ep.highlight}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Episodes() {
  usePageTitle("分集介绍");
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mysteryplot.github.io/" },
          { "@type": "ListItem", position: 2, name: "分集介绍" },
        ],
      },
      {
        "@type": "CollectionPage",
        name: "秘密教学动漫分集介绍",
        url: "https://mysteryplot.github.io/episodes",
        description: "秘密教学动漫分集剧情介绍与每话亮点伏笔速览。",
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SeoScript data={jsonLd} />
      <AnimeNavbar />
      <main className="flex-1">
        <PageHero
          tag={siteName}
          title="秘密教学动漫分集介绍"
          desc="按集整理每一话的剧情概要、核心亮点与伏笔彩蛋。无论是从头追还是补进度，这份分集指南都能帮你快速掌握全篇脉络。"
          bg={heroBg}
        />
        <nav className="border-b border-background-200 bg-background-100" aria-label="面包屑">
          <div className="mx-auto flex max-w-7xl items-center gap-1 px-4 py-3 text-sm md:px-6">
            <Link to="/" className="text-foreground-500 hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800">分集介绍</span>
          </div>
        </nav>
        <EpisodeList />
      </main>
      <AnimeFooter />
    </div>
  );
}